import { useEffect, useRef, useState, RefObject } from 'react';
import {
  Outlet,
  useMatch,
  useNavigate,
  useLocation,
  useOutletContext,
  generatePath,
  NavLink,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import queryString from 'query-string';
import {
  Checkbox,
  Tabs,
  IconLoader,
  PermissionDenied,
  PermissionEnum,
  ApplicationSelector,
  formatDate,
  apiAbortController,
  usePrevious,
  IconExpand,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { useLazyGetMessagingTransactionTypesQuery } from '../store/dictionaries/services';
import {
  useLazyGetMessagingCurrentEmployeeQuery,
  useLazyGetMessagesChannelInfoQuery,
} from './store/services';
import {
  setAssigned,
  setCurrentQuery,
  setSelectedChannel,
  setScrolled,
  setSearchQuery as setSearchQueryStore,
} from './store/slices/general';
import useGetAutocompleteList from './helpers/use-autocomplete-list';

import ROUTES from '../routes';

import cn from './messages.module.css';

const Messages = (): React.JSX.Element => {
  const [newMessageHintVisible, setNewMessageHintVisible] = useState(false);
  const [newMessage, setNewMessage] = useState<{
    id?: string;
    title?: string;
    type?: 'main' | 'requests';
  }>({});

  const {
    messages: {
      general: {
        assignedToMe,
        currentQuery,
        employee,
        selectedChannel,
        channelsInfo,
      },
    },
    messagesDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { search, pathname } = useLocation();

  const match = useMatch(ROUTES.MESSAGES);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    connectionStatus: rootConnectionStatus,
    sendMessage,
    messagesScrollRef,
  } = useOutletContext<{
    connectionStatus: string | null;
    sendMessage: SendMessage | null;
    messagesScrollRef: RefObject<HTMLDivElement | null> | null;
  }>();

  const [getMessagingCurrentEmployee] =
    useLazyGetMessagingCurrentEmployeeQuery();

  const [getMessagingTransactionTypes] =
    useLazyGetMessagingTransactionTypesQuery();
  const [getChannelInfo] = useLazyGetMessagesChannelInfoQuery();
  const [selectedUserId, setSelectedUserId] = useState('');

  const {
    autocompleteList,
    onSearch,
    isLoading: autocompleteIsLoading,
    getAutocompleteListAction,
    searchQuery,
    setSearchQuery,
  } = useGetAutocompleteList(search);

  const connectionStatus = rootConnectionStatus;

  const prevConnectionStatus = usePrevious(connectionStatus);

  const formatQuery = (query: string) => {
    const nextQueryArray = query.split('&');

    const nextQuery = nextQueryArray
      .filter(
        (item) =>
          item &&
          !item.includes('status') &&
          !item.includes('unreadMessages') &&
          !item.includes('unreadByUserId') &&
          !item.includes('employeeAssigned'),
      )
      .join('&');

    getChannelInfo(nextQuery);
  };

  const setAssignedToMe = (assigned: boolean) => {
    let query = currentQuery;

    if (assigned) {
      query = `employeeId=${employee?.id}${query ? `&${query}` : ''}`;
    } else {
      const nextQueryArray = query.split('&');

      const string = nextQueryArray
        .filter((item) => item && !item.includes('employeeId'))
        .join('&');

      query = string;
    }

    dispatch(setCurrentQuery(query));

    formatQuery(query);

    dispatch(setAssigned(assigned));
  };

  const getChannelsWithUser = ({
    organizationId,
    applicationId,
  }: {
    organizationId?: string;
    applicationId?: string;
  }) => {
    let query = currentQuery;

    if (organizationId) {
      setSelectedUserId(organizationId);

      if (query.includes('organizationId') || query.includes('applicationId')) {
        const nextQueryArray = query.split('&');

        const string = `${nextQueryArray
          .filter(
            (item) =>
              item &&
              !item.includes('organizationId') &&
              !item.includes('applicationId'),
          )
          .join('&')}&organizationId=${organizationId}`;

        query = string;
      } else {
        query = `${query ? `${query}&` : ''}organizationId=${organizationId}`;
      }
    } else if (applicationId) {
      setSelectedUserId(applicationId);

      if (query.includes('organizationId') || query.includes('applicationId')) {
        const nextQueryArray = query.split('&');

        const string = `${nextQueryArray
          .filter(
            (item) =>
              item &&
              !item.includes('organizationId') &&
              !item.includes('applicationId'),
          )
          .join('&')}applicationId=${applicationId}`;

        query = string;
      } else {
        query = `${query ? `${query}&` : ''}applicationId=${applicationId}`;
      }
    } else {
      setSelectedUserId('');

      const nextQueryArray = query.split('&');

      const string = nextQueryArray
        .filter(
          (item) =>
            item &&
            !item.includes('organizationId') &&
            !item.includes('applicationId'),
        )
        .join('&');

      query = string;
    }

    formatQuery(query);

    dispatch(setCurrentQuery(query));
  };

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(PermissionEnum.SECTION_CUSTOMERS) &&
      activePermissions.includes(PermissionEnum.MESSAGES_LIST) &&
      match
    ) {
      navigate(`${ROUTES.MESSAGES_MAIN_UNREAD}${search || ''}`);
    }
  }, [match, activePermissions, search]);

  useEffect(() => {
    if (search) {
      const parsed = queryString.parse(search);

      if (parsed.name) {
        getAutocompleteListAction(parsed.name as string);
        setSearchQuery(parsed.name as string);
      }
    }
  }, [search]);

  useEffect(() => {
    if (search && autocompleteList && autocompleteList.length) {
      const parsed = queryString.parse(search);

      if (parsed.name && autocompleteList.length === 1) {
        const body: {
          organizationId?: string;
          applicationId?: string;
        } = {};

        if (autocompleteList[0].organizationId) {
          body.organizationId = autocompleteList[0].organizationId;
        } else if (!autocompleteList[0].organizationId) {
          body.applicationId = autocompleteList[0].id;
        }

        getChannelsWithUser(body);
      }
    }
  }, [search, autocompleteList]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(PermissionEnum.SECTION_CUSTOMERS) &&
      activePermissions.includes(PermissionEnum.MESSAGES_LIST)
    ) {
      if (!search) {
        getChannelInfo('');
      }

      getMessagingCurrentEmployee();
      getMessagingTransactionTypes();
    }
  }, [activePermissions]);

  useEffect(() => {
    if (
      connectionStatus !== prevConnectionStatus &&
      prevConnectionStatus === 'Closed'
    ) {
      formatQuery(currentQuery);
    }
  }, [prevConnectionStatus, connectionStatus]);

  useEffect(() => {
    let title = 'Messages';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content: React.JSX.Element;

  content = (
    <div className={cn.messagesLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions) {
    if (
      !activePermissions?.includes(PermissionEnum.SECTION_CUSTOMERS) ||
      !activePermissions.includes(PermissionEnum.MESSAGES_LIST)
    ) {
      content = <PermissionDenied />;
    } else if (activePermissions.includes(PermissionEnum.MESSAGES_LIST)) {
      content = (
        <div className={cn.messagesContentWrapper}>
          <div className={cn.messagesContentLeft}>
            <div className={cn.messagesTop}>Chat List</div>
            <div className={cn.messagesAssignChekboxWrapper}>
              <Checkbox
                type="checkbox"
                name="assigned"
                title="Assigned to me only"
                onChange={(e) => setAssignedToMe(e.target.checked)}
                checked={assignedToMe}
              />
            </div>
            <div className={cn.messagesTabsWrapper}>
              <Tabs
                wrapperClassName={cn.messagesTabs}
                itemClassName={cn.messagesTabsItem}
                onRouterClick={() => {
                  apiAbortController.abortPendingRequest();

                  dispatch(setScrolled(false));

                  if (selectedChannel) {
                    dispatch(setSelectedChannel(undefined));

                    if (sendMessage) {
                      sendMessage(
                        JSON.stringify({
                          event: 'unsubscribe',
                          data: { channelId: selectedChannel.id },
                        }),
                      );
                    }
                  }
                }}
                tabs={[
                  {
                    name: (
                      <div className={cn.messageTabItemTitle}>
                        {`Main chat ${
                          channelsInfo ? `(${channelsInfo?.mainChannels})` : ''
                        }`}
                      </div>
                    ),
                    path: `${ROUTES.MESSAGES_MAIN}${search || ''}`,
                  },
                  {
                    name: (
                      <div className={cn.messageTabItemTitle}>
                        {`Requests ${
                          channelsInfo
                            ? `(${channelsInfo?.requestChannels})`
                            : ''
                        }`}
                      </div>
                    ),
                    path: `${ROUTES.MESSAGES_REQUEST}${search || ''}`,
                  },
                ]}
                enableRouting
              />
            </div>
            {activePermissions?.includes(
              PermissionEnum.INDIVIDUAL_CUSTOMERS_LIST,
            ) &&
              activePermissions?.includes(
                PermissionEnum.CORPORATE_CUSTOMERS_LIST,
              ) && (
                <div className={cn.searchWrapper}>
                  <ApplicationSelector
                    placeholder="Enter customer or company name"
                    list={autocompleteList.map((item) => ({
                      ...item,
                      dob:
                        formatDate(item?.dob || '') ||
                        formatDate(item?.dateOfIncorporation || '') ||
                        '-',
                    }))}
                    onSearch={onSearch}
                    isLoading={autocompleteIsLoading}
                    onApplicationClick={(id: string) => {
                      const currentItem = autocompleteList?.find(
                        (item) => item.id === id,
                      );

                      if (currentItem) {
                        const body: {
                          organizationId?: string;
                          applicationId?: string;
                        } = {};

                        if (currentItem?.organizationId) {
                          body.organizationId = currentItem.organizationId;
                        } else if (!currentItem?.organizationId) {
                          body.applicationId = currentItem.id;
                        }

                        getChannelsWithUser(body);
                        dispatch(
                          setSearchQueryStore(
                            (body.organizationId as string) ||
                              (body.applicationId as string),
                          ),
                        );
                      } else {
                        getChannelsWithUser({});

                        dispatch(setSearchQueryStore(''));

                        if (search) {
                          navigate(pathname);
                        }
                      }

                      if (searchQuery) {
                        setSearchQuery('');
                        setSearchQueryStore('');
                        getAutocompleteListAction('');
                      }
                    }}
                    onButtonClick={() => {}}
                    searchQuery={searchQuery}
                    fullWidth
                    withoutButton
                    withClearButton
                  />
                </div>
              )}

            <Outlet
              context={{
                selectedUserId,
                sendMessage,
                scrollRef: messagesScrollRef || scrollRef,
                search,
                connectionStatus,
                prevConnectionStatus,
              }}
            />
          </div>
        </div>
      );
    }
  }

  return (
    <div className={cn.messages}>
      {content}
      {newMessageHintVisible && (
        <div className={`${cn.messageLoadError} ${cn.withMoreWidth}`}>
          <span>
            New message in&nbsp;
            <NavLink
              className={cn.newMessageLink}
              to={generatePath(
                newMessage.type === 'main'
                  ? ROUTES.MESSAGES_MAIN_UNREAD_MESSAGE
                  : ROUTES.MESSAGES_REQUEST_UNREAD_MESSAGE,
                { messageId: newMessage.id || ('') },
              )}
              onClick={() => {
                setNewMessageHintVisible(false);
                setNewMessage({});
              }}
            >
              {newMessage.title}
            </NavLink>
          </span>
          <button
            type="button"
            className={cn.buttonClose}
            aria-label="Close button"
            onClick={() => {
              setNewMessageHintVisible(false);
              setNewMessage({});
            }}
          >
            <IconExpand />
          </button>
        </div>
      )}
      <div id="employee-popup" />
    </div>
  );
};

const MessagesWrapper = () => <Messages />;

export default MessagesWrapper;
